<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  type CaregiverRankingRow, type EmploymentContractType,
} from '@/api/patient-monthly-metrics';
import { formatCurrency } from '@/utils/currency';
import { getCareerColor } from '@/utils/careers';
import { useMobile } from '@/composables/useMobile';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import { mergeCostGroups } from '@/utils/cost-groups';
import PatientCostGroupsTable from './patient-cost-groups-table.vue';

const props = defineProps<{
  rows: CaregiverRankingRow[];
  showCosts: boolean;
}>();

const { mdAndUp } = useMobile();
const { tableProps } = useResponsiveTable({ stack: false });

const CONTRACT_CHIPS: Record<EmploymentContractType, { label: string; color: string }> = {
  'tax_bill': { label: 'Honorarios', color: 'deep-purple' },
  'contract': { label: 'Contrato', color: 'teal' },
  'without_contract': { label: 'Sin contrato', color: 'grey' },
};

function contractChip(row: CaregiverRankingRow) {
  return CONTRACT_CHIPS[row.employmentContractType] ?? CONTRACT_CHIPS.without_contract;
}

const headers = computed(() => {
  const base = [
    { title: 'Profesional', value: 'fullName', sortable: false },
    { title: 'Especialidad', value: 'humanCareer', sortable: false },
    { title: 'Tipo Contrato', value: 'employmentContractType', sortable: false },
    {
      title: 'Frecuencia (Turnos)',
      value: 'shiftsCount',
      sortable: false,
      cellProps: { class: 'font-weight-bold text-center' },
    },
  ];
  if (props.showCosts) {
    base.push({ title: 'Costo Generado', value: 'totalCost', sortable: false });
  }

  return base;
});

// VDataTable declares `expanded` as string[] even though it stores raw
// item-values (our numeric caregiverIds) at runtime.
const expanded = ref<string[]>([]);

// Whole-row click toggles the breakdown; the expand button keeps working on its
// own, so clicks originating from it are skipped to avoid a double toggle.
function toggleRowExpansion(event: Event, row: { item: CaregiverRankingRow }) {
  if (!props.showCosts) return;
  if ((event.target as HTMLElement).closest('button')) return;

  const id = row.item.caregiverId as unknown as string;
  expanded.value = expanded.value.includes(id) ?
    expanded.value.filter((value) => value !== id) :
    [...expanded.value, id];
}

// Mobile: tap a card to toggle its inline cost breakdown.
const expandedCards = ref<Set<number>>(new Set());

function toggleCard(caregiverId: number) {
  if (!props.showCosts) return;
  if (expandedCards.value.has(caregiverId)) {
    expandedCards.value.delete(caregiverId);
  } else {
    expandedCards.value.add(caregiverId);
  }
  expandedCards.value = new Set(expandedCards.value);
}
</script>

<template>
  <v-card
    elevation="2"
    class="pa-4"
  >
    <div class="mb-1">
      <span class="text-subtitle-1 font-weight-bold">Detalle de Profesionales y Frecuencia</span>
    </div>
    <p class="text-caption text-medium-emphasis mb-3">
      Profesionales que atendieron al paciente en el mes, ordenados por
      {{ showCosts ? 'costo generado' : 'cantidad de turnos' }}.
    </p>
    <p
      v-if="rows.length === 0"
      class="text-body-2 text-medium-emphasis text-center py-4"
    >
      Sin profesionales con turnos en este mes.
    </p>
    <v-data-table
      v-else-if="mdAndUp"
      v-bind="tableProps"
      v-model:expanded="expanded"
      :headers="headers"
      :header-props="{ class: 'font-weight-bold' }"
      :items="rows"
      item-value="caregiverId"
      :show-expand="showCosts"
      :row-props="showCosts ? { class: 'cursor-pointer' } : undefined"
      @click:row="toggleRowExpansion"
    >
      <template #[`item.fullName`]="{ item }">
        <span class="font-weight-medium">{{ item.fullName }}</span>
      </template>
      <template #[`item.humanCareer`]="{ item }">
        <v-chip
          size="small"
          :color="getCareerColor(item.career)"
          variant="tonal"
          label
        >
          {{ item.humanCareer }}
        </v-chip>
      </template>
      <template #[`item.employmentContractType`]="{ item }">
        <v-chip
          size="small"
          :color="contractChip(item).color"
          variant="tonal"
          label
        >
          {{ contractChip(item).label }}
        </v-chip>
      </template>
      <template #[`item.totalCost`]="{ item }">
        <span class="font-weight-bold">{{ formatCurrency(item.totalCost ?? 0) }}</span>
      </template>
      <!-- Real table rows so the breakdown shares the parent's column grid:
           cost/turnos/total land under Tipo Contrato / Frecuencia / Costo Generado. -->
      <template #expanded-row="{ item }">
        <tr class="text-caption text-medium-emphasis">
          <td colspan="2" />
          <td>Costo Bruto por turno</td>
          <td class="text-center">
            Cantidad de Turnos
          </td>
          <td>Costo Bruto Total</td>
          <td />
        </tr>
        <tr
          v-for="costRow in mergeCostGroups(item.costGroups ?? [])"
          :key="costRow.cost"
        >
          <td colspan="2" />
          <td>{{ formatCurrency(costRow.cost) }}</td>
          <td class="text-center font-weight-bold">
            {{ costRow.totalShifts }}
          </td>
          <td class="font-weight-bold">
            {{ formatCurrency(costRow.totalCost) }}
          </td>
          <td />
        </tr>
      </template>
    </v-data-table>
    <template v-else>
      <v-card
        v-for="row in rows"
        :key="row.caregiverId"
        :elevation="1"
        class="pa-3 mb-3"
        :class="{ 'cursor-pointer': showCosts }"
        data-testid="ranking-card"
        @click="toggleCard(row.caregiverId)"
      >
        <div class="d-flex align-center justify-space-between ga-1">
          <span class="font-weight-medium">{{ row.fullName }}</span>
          <v-icon
            v-if="showCosts"
            size="small"
            :icon="expandedCards.has(row.caregiverId) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
          />
        </div>
        <div class="d-flex flex-wrap ga-1 mt-2">
          <v-chip
            size="x-small"
            :color="getCareerColor(row.career)"
            variant="tonal"
            label
          >
            {{ row.humanCareer }}
          </v-chip>
          <v-chip
            size="x-small"
            :color="contractChip(row).color"
            variant="tonal"
            label
          >
            {{ contractChip(row).label }}
          </v-chip>
        </div>
        <div class="d-flex justify-space-between mt-2">
          <span class="text-caption text-medium-emphasis">
            {{ row.shiftsCount }} {{ row.shiftsCount === 1 ? 'turno' : 'turnos' }}
          </span>
          <span
            v-if="showCosts"
            class="text-caption font-weight-bold"
          >
            {{ formatCurrency(row.totalCost ?? 0) }}
          </span>
        </div>
        <patient-cost-groups-table
          v-if="showCosts && expandedCards.has(row.caregiverId)"
          :groups="row.costGroups ?? []"
          class="mt-2"
        />
      </v-card>
    </template>
  </v-card>
</template>
